import { createContext } from "react";
import type { AuthSession } from "@/types";

/**
 * Authentication context value.
 *
 * Source: ENG-AUTH-LOGIN-SIGNUP frontend half. The Provider sources
 * its data from the ``GET /api/v1/auth/me`` query — the cookie is
 * httpOnly, so this context is the only readable surface the React
 * tree has into the session.
 *
 * Split from the Provider component + consumer hook so each module
 * satisfies React Fast Refresh's "components-only" export rule
 * (mirrors selectedAppContextValue.ts).
 */

export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

export interface AuthContextValue {
  status: AuthStatus;
  session: AuthSession | null;
  isAuthenticated: boolean;
  /** Refresh the /me query — useful after login + signup mutations. */
  refresh: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

/** TanStack Query key for the /me session query. Lives here so consumers
 *  (login mutation, signup mutation) can invalidate the cache without
 *  importing the AuthProvider component (Fast Refresh "components-only"
 *  export rule). */
export const AUTH_SESSION_QUERY_KEY = ["auth", "me"] as const;

/**
 * Runtime type-guard for a COMPLETE session envelope.
 *
 * ``AuthSession`` declares ``user`` + ``workspace`` as non-optional, so
 * ``tsc`` happily accepts ``session?.workspace.workspace_id`` — the
 * optional chain guards the session but NOT the nested object. That type
 * is a promise about the *contract*, not about the *bytes on the wire*:
 * ``GET /api/v1/auth/me`` is deserialized straight into ``AuthSession``
 * with no runtime validation, so a 200 whose body lacks ``workspace``
 * throws ``TypeError`` in whichever consumer reads the second hop first.
 * ``SelectedAppProvider`` sits directly under ``ProtectedRoute``, so that
 * throw takes down the WHOLE authenticated shell via the top-level
 * ErrorBoundary instead of degrading or bouncing to /login.
 *
 * The backend cannot legitimately produce such a body — ``AuthSessionResponse``
 * (backend/app/schemas/auth.py) declares ``user`` + ``workspace`` as required
 * pydantic fields, so pydantic would raise before serializing. A malformed
 * 200 is therefore always infrastructure interference (proxy/CDN returning a
 * cached or misrouted body, a service worker, a dev route stub) — never a
 * real user in a real state. That is what makes "treat it as unauthenticated"
 * safe: it cannot strand a legitimate session, it only converts an
 * already-broken state from a dead-end white screen into a /login bounce.
 *
 * Takes ``unknown`` deliberately: typing the parameter as ``AuthSession``
 * would let TypeScript "prove" the very fields we are checking, making the
 * guard look redundant to a future reader and inviting its removal.
 */
export function isCompleteSession(value: unknown): value is AuthSession {
  if (typeof value !== "object" || value === null) return false;
  const { user, workspace } = value as {
    user?: unknown;
    workspace?: unknown;
  };
  return (
    typeof user === "object" &&
    user !== null &&
    typeof (user as { user_id?: unknown }).user_id === "string" &&
    typeof workspace === "object" &&
    workspace !== null &&
    typeof (workspace as { workspace_id?: unknown }).workspace_id === "string"
  );
}
